import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  FileText, 
  Download, 
  Eye, 
  CheckCircle2, 
  Globe,
  Sparkles,
  Edit3,
  Plus,
  Save,
  Library,
  Folder,
  Users,
  X,
  Trash2,
  Building2
} from "lucide-react";

import TemplateLibrary from "@/components/TemplateLibrary";
import ClauseLibrary from "@/components/ClauseLibrary";
import DraggableClauseList from "@/components/DraggableClauseList";
import ClientSelector from "@/components/ClientSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ContractDrafting({ user, cabinet, onLogout }) {
  const navigate = useNavigate();
  
  // UI State
  const [language, setLanguage] = useState("fr");
  const [loading, setLoading] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showClauseLibrary, setShowClauseLibrary] = useState(false);
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [selectingPartyIndex, setSelectingPartyIndex] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);  // NEW
  
  // Contract State
  const [title, setTitle] = useState("");
  const [parties, setParties] = useState([
    { id: 1, name: "", representative: "", address: "" },
    { id: 2, name: "", representative: "", address: "" }
  ]);
  const [selectedClauses, setSelectedClauses] = useState([]);
  const [editingClause, setEditingClause] = useState(null);
  const [exportFormat, setExportFormat] = useState("pdf");
  
  // Template State
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [isCabinetWide, setIsCabinetWide] = useState(false);

  // Autosave
  useEffect(() => {
    const timer = setTimeout(() => {
      if (title || selectedClauses.length > 0) {
        localStorage.setItem('contract_draft_autosave', JSON.stringify({
          title,
          parties,
          selectedClauses,
          language,
          timestamp: Date.now()
        }));
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [title, selectedClauses, parties, language]);

  // Load autosave on mount
  useEffect(() => {
    const saved = localStorage.getItem('contract_draft_autosave');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        const ageMinutes = (Date.now() - data.timestamp) / 1000 / 60;
        
        if (ageMinutes < 60) {
          setTitle(data.title || "");
          setParties(data.parties || [{ id: 1, name: "", representative: "", address: "" }, { id: 2, name: "", representative: "", address: "" }]);
          setSelectedClauses(data.selectedClauses || []);
          setLanguage(data.language || "fr");
          toast.success(language === "fr" ? "Brouillon restauré" : "Draft restored");
        }
      } catch (e) {
        console.error("Error loading autosave:", e);
      }
    }
  }, []);

  const getLocalizedText = (text, lang = language) => {
    if (typeof text === 'object' && text !== null) {
      return text[lang] || text['fr'] || text['en'] || Object.values(text)[0];
    }
    return text;
  };

  const handleSelectTemplate = async (template) => {
    try {
      const token = localStorage.getItem('lawliet_token');
      
      const response = await axios.get(`${API}/templates/${template.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fullTemplate = response.data;
      setCurrentTemplate(fullTemplate);
      setTitle(getLocalizedText(fullTemplate.name));
      setLanguage(fullTemplate.default_language || "fr");
      
      const clausesWithDetails = [];
      for (const templateClause of fullTemplate.clauses || []) {
        try {
          const clauseResponse = await axios.get(`${API}/clauses/library/${templateClause.clause_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          clausesWithDetails.push({
            ...clauseResponse.data,
            order: templateClause.order,
            is_included: templateClause.is_included,
            is_required: clauseResponse.data.is_mandatory,
            local_modifications: templateClause.local_modifications
          });
        } catch (err) {
          console.error(`Error loading clause ${templateClause.clause_id}:`, err);
        }
      }
      
      clausesWithDetails.sort((a, b) => a.order - b.order);
      setSelectedClauses(clausesWithDetails);
      
      toast.success(language === "fr" ? "Template chargé" : "Template loaded");
    } catch (error) {
      console.error("Error loading template:", error);
      toast.error(language === "fr" ? "Erreur lors du chargement du template" : "Error loading template");
    }
  };

  const handleAddClause = (clause) => {
    if (selectedClauses.find(c => c.id === clause.id)) {
      toast.info(language === "fr" ? "Clause déjà ajoutée" : "Clause already added");
      return;
    }
    
    const nextOrder = selectedClauses.length > 0 
      ? Math.max(...selectedClauses.map(c => c.order || 0)) + 1 
      : 1;
    
    setSelectedClauses([...selectedClauses, {
      ...clause,
      order: nextOrder,
      is_required: clause.is_mandatory,
      is_locally_modified: false
    }]);
  };

  const handleReorderClauses = (newOrder) => {
    const reordered = newOrder.map((clause, index) => ({
      ...clause,
      order: index + 1
    }));
    setSelectedClauses(reordered);
  };

  const handleEditClause = (clause) => {
    setEditingClause(clause);
  };

  const handleRemoveClause = (clauseId) => {
    setSelectedClauses(selectedClauses.filter(c => c.id !== clauseId));
    toast.success(language === "fr" ? "Clause supprimée" : "Clause removed");
  };

  const handleAddParty = () => {
    const newId = parties.length > 0 ? Math.max(...parties.map(p => p.id)) + 1 : 1;
    setParties([...parties, { id: newId, name: "", representative: "", address: "", legal_form: "", capital: "", rcs: "", siren: "", representative_title: "", designation: "" }]);
    toast.success(language === "fr" ? "Partie ajoutée" : "Party added");
  };

  const handleRemoveParty = (partyId) => {
    if (parties.length <= 2) {
      toast.error(language === "fr" ? "Minimum 2 parties requises" : "Minimum 2 parties required");
      return;
    }
    setParties(parties.filter(p => p.id !== partyId));
    toast.success(language === "fr" ? "Partie supprimée" : "Party removed");
  };

  const handlePartyChange = (partyId, field, value) => {
    setParties(parties.map(p => 
      p.id === partyId ? { ...p, [field]: value } : p
    ));
  };
  
  const handleSelectClient = (client) => {
    if (selectingPartyIndex !== null) {
      // Fill selected party with client data
      setParties(parties.map((p, idx) => 
        idx === selectingPartyIndex ? {
          ...p,
          name: client.name,
          legal_form: client.legal_form,
          capital: client.capital,
          rcs: client.rcs,
          siren: client.siren,
          address: client.address,
          representative: client.representative_name,
          representative_title: client.representative_title,
          designation: client.designation || ""
        } : p
      ));
      toast.success(language === "fr" ? "Client sélectionné" : "Client selected");
      setSelectingPartyIndex(null);
    }
  };

  const generatePreview = () => {
    if (selectedClauses.length === 0) return "";
    
    let html = '<div style="max-width: 210mm; margin: 0 auto; padding: 30mm 25mm; background: white; font-family: -apple-system, BlinkMacSystemFont, \'Inter\', \'Segoe UI\', sans-serif; color: #0f172a; line-height: 1.75;">';
    
    // Header avec logo
    html += `
      <div style="text-align: center; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px double #1E40AF;">
        <div style="margin-bottom: 15px;">
          <img src="/lawliet-mascot.png" style="height: 50px; width: auto; display: inline-block;" alt="Lawliet.ai" />
        </div>
        <h1 style="font-size: 28px; font-weight: 700; color: #1E40AF; margin: 0; letter-spacing: 0.5px; text-transform: uppercase;">
          ${title || '[TITRE DU CONTRAT]'}
        </h1>
        <p style="font-size: 11px; color: #64748B; margin-top: 8px; font-style: italic;">
          Document juridique professionnel
        </p>
      </div>
    `;
    
    // Metadata parties
    if (parties.some(p => p.name)) {
      html += '<div style="background: #F8FAFC; border-left: 4px solid #1E40AF; padding: 15px 20px; margin-bottom: 30px;">';
      html += '<p style="font-size: 12px; color: #475569; margin: 0; font-weight: 600; margin-bottom: 8px;">PARTIES AU CONTRAT :</p>';
      parties.filter(p => p.name).forEach((p, idx) => {
        html += `<p style="font-size: 11px; color: #64748B; margin: 4px 0;">• ${p.name}`;
        if (p.representative) html += ` (repr. ${p.representative})`;
        html += `</p>`;
      });
      html += '</div>';
    }
    
    // Clauses avec numérotation automatique
    let articleNumber = 1;
    for (const clause of selectedClauses) {
      let clauseText = clause.modified_content || getLocalizedText(clause.content);
      
      // Replace party variables
      parties.forEach((party, idx) => {
        const prefix = `party${idx + 1}`;
        clauseText = clauseText.replaceAll(`{{${prefix}_name}}`, party.name || `<span style="background: #FEF3C7; padding: 2px 6px; border-radius: 3px;">[Partie ${idx + 1}]</span>`);
        clauseText = clauseText.replaceAll(`{{${prefix}_legal_form}}`, party.legal_form || '[forme]');
        clauseText = clauseText.replaceAll(`{{${prefix}_capital}}`, party.capital || '[capital]');
        clauseText = clauseText.replaceAll(`{{${prefix}_rcs}}`, party.rcs || '[RCS]');
        clauseText = clauseText.replaceAll(`{{${prefix}_siren}}`, party.siren || '[SIREN]');
        clauseText = clauseText.replaceAll(`{{${prefix}_address}}`, party.address || '<span style="background: #FEF3C7; padding: 2px 6px; border-radius: 3px;">[adresse]</span>');
        clauseText = clauseText.replaceAll(`{{${prefix}_representative}}`, party.representative || '[représentant]');
        clauseText = clauseText.replaceAll(`{{${prefix}_title}}`, party.representative_title || '[titre]');
        clauseText = clauseText.replaceAll(`{{${prefix}_designation}}`, party.designation || '[désignation]');
      });
      
      // Format paragraphs
      const paragraphs = clauseText.split('\n\n');
      paragraphs.forEach(para => {
        if (!para.trim()) return;
        
        const trimmed = para.trim();
        
        // Article titles with automatic numbering
        if (trimmed.startsWith('Article') || trimmed.startsWith('ARTICLE')) {
          // Extract and replace article number
          const articleText = trimmed.replace(/Article\s*\d*\s*-?/i, `Article ${articleNumber} -`);
          html += `
            <h2 style="font-size: 16px; font-weight: 700; color: #1E40AF; margin: 35px 0 15px 0; padding-top: 25px; border-top: 1px solid #E2E8F0;">
              ${articleText}
            </h2>
          `;
          articleNumber++;
        }
        // Section titles (all caps)
        else if (trimmed.toUpperCase() === trimmed && trimmed.length < 100 && trimmed.length > 5) {
          html += `
            <p style="text-align: center; font-weight: 700; margin: 25px 0 15px 0; font-size: 13px; color: #334155; letter-spacing: 1px;">
              ${trimmed}
            </p>
          `;
        }
        // Sub-sections starting with numbers/letters
        else if (/^[\d\.]+\s/.test(trimmed) || /^\([a-z]\)/.test(trimmed)) {
          html += `
            <p style="margin: 12px 0 8px 20px; font-size: 13px; color: #334155;">
              ${para.replace(/\n/g, '<br/>')}
            </p>
          `;
        }
        // Normal paragraphs
        else {
          html += `
            <p style="text-align: justify; margin: 12px 0; line-height: 1.9; font-size: 13px; color: #1E293B;">
              ${para.replace(/\n/g, '<br/>')}
            </p>
          `;
        }
      });
    }
    
    // Signature block
    html += `
      <div style="margin-top: 60px; page-break-inside: avoid;">
        <p style="text-align: center; font-style: italic; font-size: 12px; color: #64748B; margin-bottom: 30px;">
          Fait en ${parties.length} exemplaires originaux
        </p>
        <div style="display: grid; grid-template-columns: repeat(${Math.min(parties.length, 2)}, 1fr); gap: 40px; margin-top: 40px;">
    `;
    
    parties.filter(p => p.name).forEach((party, idx) => {
      html += `
        <div style="text-align: center;">
          <p style="font-weight: 600; font-size: 12px; margin-bottom: 40px; color: #334155;">
            Pour ${party.name}
          </p>
          <div style="border-bottom: 2px solid #94A3B8; width: 200px; margin: 0 auto;"></div>
          <p style="font-size: 11px; margin-top: 8px; color: #64748B;">
            ${party.representative || '[Représentant]'}<br/>
            ${party.representative_title || '[Titre]'}
          </p>
        </div>
      `;
    });
    
    html += '</div></div>';
    
    // Footer
    html += `
      <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #E2E8F0; text-align: center;">
        <p style="font-size: 9px; color: #94A3B8; font-style: italic;">
          Document généré le ${new Date().toLocaleDateString('fr-FR', {day: '2-digit', month: 'long', year: 'numeric'})} à ${new Date().toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}<br/>
          via Lawliet.ai - Plateforme juridique professionnelle
        </p>
      </div>
    `;
    
    html += '</div>';
    return html;
  };

  const generateContract = async () => {
    if (!title.trim()) {
      toast.error(language === "fr" ? "Veuillez donner un titre" : "Please provide a title");
      return;
    }
    
    if (selectedClauses.length === 0) {
      toast.error(language === "fr" ? "Ajoutez au moins une clause" : "Add at least one clause");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      
      // Generate text version for backend
      let textVersion = '';
      for (const clause of selectedClauses) {
        let clauseText = clause.modified_content || getLocalizedText(clause.content);
        
        // Replace party variables
        parties.forEach((party, idx) => {
          const prefix = `party${idx + 1}`;
          clauseText = clauseText.replaceAll(`{{${prefix}_name}}`, party.name || `[Partie ${idx + 1}]`);
          clauseText = clauseText.replaceAll(`{{${prefix}_representative}}`, party.representative || '[...]');
          clauseText = clauseText.replaceAll(`{{${prefix}_address}}`, party.address || '[...]');
        });
        
        textVersion += clauseText + '\n\n';
      }
      
      const metadata = {
        parties: parties.filter(p => p.name).map(p => p.name)
      };
      
      const response = await axios.post(
        `${API}/contracts/export`,
        {
          contract_text: textVersion,
          title: title,
          format: exportFormat,
          metadata: metadata
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { 
        type: exportFormat === 'pdf' 
          ? 'application/pdf' 
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}_${language}.${exportFormat}`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(language === "fr" ? `Contrat exporté en ${exportFormat.toUpperCase()} !` : `Contract exported as ${exportFormat.toUpperCase()}!`);
      
      localStorage.removeItem('contract_draft_autosave');
    } catch (error) {
      console.error(error);
      toast.error(language === "fr" ? "Erreur lors de la génération" : "Error generating contract");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error(language === "fr" ? "Donnez un nom au template" : "Provide a template name");
      return;
    }
    
    if (selectedClauses.length === 0) {
      toast.error(language === "fr" ? "Ajoutez au moins une clause" : "Add at least one clause");
      return;
    }

    try {
      const token = localStorage.getItem('lawliet_token');
      
      const templateData = {
        name: { fr: templateName, en: templateName },
        description: { 
          fr: templateDescription || `Template créé le ${new Date().toLocaleDateString('fr-FR')}`,
          en: templateDescription || `Template created on ${new Date().toLocaleDateString('en-US')}`
        },
        category: "custom",
        clauses: selectedClauses.map(clause => ({
          clause_id: clause.id,
          order: clause.order,
          is_included: true,
          local_modifications: clause.modified_content ? { [language]: clause.modified_content } : null
        })),
        required_variables: [],
        optional_variables: [],
        default_language: language,
        is_cabinet_wide: isCabinetWide
      };
      
      await axios.post(`${API}/templates`, templateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const message = isCabinetWide
        ? (language === "fr" ? "Template sauvegardé ! En attente de validation admin." : "Template saved! Pending admin approval.")
        : (language === "fr" ? "Template personnel sauvegardé !" : "Personal template saved!");
      
      toast.success(message);
      setShowSaveTemplateModal(false);
      setTemplateName("");
      setTemplateDescription("");
      setIsCabinetWide(false);
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error(language === "fr" ? "Erreur lors de la sauvegarde" : "Error saving template");
    }
  };

  const reset = () => {
    if (window.confirm(language === "fr" ? "Tout effacer ?" : "Clear everything?")) {
      setTitle("");
      setParties([{ id: 1, name: "", representative: "", address: "" }, { id: 2, name: "", representative: "", address: "" }]);
      setSelectedClauses([]);
      setCurrentTemplate(null);
      localStorage.removeItem('contract_draft_autosave');
      toast.success(language === "fr" ? "Réinitialisé" : "Reset");
    }
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-serif text-3xl font-semibold tracking-tight text-slate-900">
                  {language === "fr" ? "Rédaction de Contrats" : "Contract Drafting"}
                </h1>
                <p className="text-sm text-slate-600 mt-1">
                  {language === "fr" 
                    ? "Créez des contrats juridiques avec intelligence artificielle"
                    : "Create legal contracts with artificial intelligence"}
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => setLanguage("fr")}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition ${language === "fr" ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    FR
                  </button>
                  <button
                    onClick={() => setLanguage("en")}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition ${language === "en" ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    EN
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowTemplateLibrary(true)}
                variant="outline"
                className="gap-2"
              >
                <Folder className="h-4 w-4" />
                {language === "fr" ? "Parcourir templates" : "Browse templates"}
              </Button>
              
              <Button
                onClick={() => setShowSaveTemplateModal(true)}
                variant="outline"
                className="gap-2"
                disabled={selectedClauses.length === 0}
              >
                <Save className="h-4 w-4" />
                {language === "fr" ? "Sauvegarder comme template" : "Save as template"}
              </Button>
              
              <div className="flex-1"></div>
              
              <Button
                onClick={reset}
                variant="outline"
                size="sm"
              >
                {language === "fr" ? "Réinitialiser" : "Reset"}
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Title & Parties (1/3) */}
            <div className="lg:col-span-1 space-y-6">
              {/* Title */}
              <Card>
                <CardContent className="pt-6">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    {language === "fr" ? "Titre du contrat" : "Contract title"} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={language === "fr" ? "Ex: Contrat de Prestation - Client XYZ" : "Ex: Service Agreement - Client XYZ"}
                    className="text-base"
                  />
                </CardContent>
              </Card>

              {/* Parties - Compact */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold">
                      {language === "fr" ? "Parties" : "Parties"}
                    </CardTitle>
                    <Button onClick={handleAddParty} size="sm" variant="outline" className="h-7 gap-1">
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                  {parties.map((party, index) => {
                    const colors = [
                      { bg: "bg-blue-50", border: "border-blue-200" },
                      { bg: "bg-green-50", border: "border-green-200" },
                      { bg: "bg-purple-50", border: "border-purple-200" },
                      { bg: "bg-amber-50", border: "border-amber-200" }
                    ];
                    const color = colors[index % colors.length];
                    
                    return (
                      <div key={party.id} className={`p-3 ${color.bg} rounded-lg border ${color.border} space-y-2`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold">{language === "fr" ? `Partie ${index + 1}` : `Party ${index + 1}`}</span>
                          <div className="flex gap-1">
                            <Button onClick={() => {setSelectingPartyIndex(index);setShowClientSelector(true);}} size="sm" variant="outline" className="h-7 text-xs gap-1">
                              <Building2 className="h-3 w-3" />
                              {language === "fr" ? "Client" : "Client"}
                            </Button>
                            {parties.length > 2 && (
                              <Button onClick={() => handleRemoveParty(party.id)} size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600">
                                <X className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* All fields */}
                        <Input value={party.name} onChange={(e) => handlePartyChange(party.id, 'name', e.target.value)} placeholder={language === "fr" ? "Raison sociale*" : "Company name*"} className="text-sm bg-white" />
                        
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={party.legal_form || ''} onChange={(e) => handlePartyChange(party.id, 'legal_form', e.target.value)} placeholder="Forme (SAS...)" className="text-sm bg-white" />
                          <Input value={party.capital || ''} onChange={(e) => handlePartyChange(party.id, 'capital', e.target.value)} placeholder="Capital (€)" className="text-sm bg-white" />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={party.siren || ''} onChange={(e) => handlePartyChange(party.id, 'siren', e.target.value)} placeholder="SIREN" className="text-sm bg-white" maxLength={9} />
                          <Input value={party.rcs || ''} onChange={(e) => handlePartyChange(party.id, 'rcs', e.target.value)} placeholder="RCS" className="text-sm bg-white" />
                        </div>
                        
                        <Input value={party.address} onChange={(e) => handlePartyChange(party.id, 'address', e.target.value)} placeholder={language === "fr" ? "Adresse complète du siège" : "Full address"} className="text-sm bg-white" />
                        
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={party.representative} onChange={(e) => handlePartyChange(party.id, 'representative', e.target.value)} placeholder={language === "fr" ? "Représentant" : "Representative"} className="text-sm bg-white" />
                          <Input value={party.representative_title || ''} onChange={(e) => handlePartyChange(party.id, 'representative_title', e.target.value)} placeholder="Fonction" className="text-sm bg-white" />
                        </div>
                        
                        <Input value={party.designation || ''} onChange={(e) => handlePartyChange(party.id, 'designation', e.target.value)} placeholder={language === "fr" ? "Désignation (la Société...)" : "Designation"} className="text-sm bg-white" />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Clauses & Export (2/3) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Clauses */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold">
                      {language === "fr" ? "Clauses du contrat" : "Contract clauses"}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        {selectedClauses.length}
                      </Badge>
                      <Button 
                        onClick={() => setShowClauseLibrary(true)} 
                        size="sm" 
                        className="gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        {language === "fr" ? "Ajouter" : "Add"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {selectedClauses.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm mb-4">
                        {language === "fr" ? "Aucune clause ajoutée" : "No clauses added"}
                      </p>
                      <p className="text-xs text-slate-400">
                        {language === "fr" ? "Cliquez sur le bouton ci-dessus pour ajouter des clauses" : "Click the button above to add clauses"}
                      </p>
                    </div>
                  ) : (
                    <DraggableClauseList
                      clauses={selectedClauses}
                      onReorder={handleReorderClauses}
                      onEdit={handleEditClause}
                      onRemove={handleRemoveClause}
                      language={language}
                      getLocalizedText={getLocalizedText}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Export */}
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-3 block">
                      {language === "fr" ? "Format d'export professionnel" : "Professional export format"}
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setExportFormat("pdf")}
                        className={`p-4 border-2 rounded-lg transition ${exportFormat === "pdf" ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-300"}`}
                      >
                        <FileText className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                        <p className="text-sm font-semibold">PDF</p>
                        <p className="text-xs text-slate-600 mt-1">{language === "fr" ? "Non-éditable" : "Read-only"}</p>
                      </button>
                      <button
                        onClick={() => setExportFormat("docx")}
                        className={`p-4 border-2 rounded-lg transition ${exportFormat === "docx" ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-300"}`}
                      >
                        <FileText className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                        <p className="text-sm font-semibold">Word</p>
                        <p className="text-xs text-slate-600 mt-1">{language === "fr" ? "Éditable" : "Editable"}</p>
                      </button>
                    </div>
                  </div>

                  <Button
                    onClick={generateContract}
                    className="w-full bg-blue-600 hover:bg-blue-700 gap-2 h-12"
                    disabled={loading || !title.trim() || selectedClauses.length === 0}
                  >
                    {loading ? (
                      <>{language === "fr" ? "Génération..." : "Generating..."}</>
                    ) : (
                      <>
                        <Download className="h-5 w-5" />
                        {language === "fr" ? `Générer ${exportFormat.toUpperCase()}` : `Generate ${exportFormat.toUpperCase()}`}
                      </>
                    )}
                  </Button>
                  
                  {/* Preview Button */}
                  <Button
                    onClick={() => setShowPreviewModal(true)}
                    variant="outline"
                    className="w-full gap-2"
                    disabled={selectedClauses.length === 0}
                  >
                    <Eye className="h-4 w-4" />
                    {language === "fr" ? "Prévisualiser le document" : "Preview document"}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal - NEW */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowPreviewModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Eye className="h-5 w-5 text-blue-600" />
                <h2 className="text-xl font-semibold">
                  {language === "fr" ? "Prévisualisation Professionnelle" : "Professional Preview"}
                </h2>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                  {exportFormat.toUpperCase()}
                </Badge>
              </div>
              <button onClick={() => setShowPreviewModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-8 bg-slate-50">
              <div className="max-w-4xl mx-auto bg-white shadow-lg" style={{ fontFamily: 'Georgia, serif' }}>
                <div className="p-12">
                  {generatePreview() ? (
                    <div dangerouslySetInnerHTML={{ __html: generatePreview() }} />
                  ) : (
                    <div className="text-center py-20 text-slate-400">
                      <Eye className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <p>{language === "fr" ? "Aucun contenu à prévisualiser" : "No content to preview"}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modals */}
      <TemplateLibrary
        isOpen={showTemplateLibrary}
        onClose={() => setShowTemplateLibrary(false)}
        onSelectTemplate={handleSelectTemplate}
        language={language}
      />
      
      <ClauseLibrary
        isOpen={showClauseLibrary}
        onClose={() => setShowClauseLibrary(false)}
        onAddClause={handleAddClause}
        language={language}
      />
      
      {/* Client Selector - NEW */}
      <ClientSelector
        isOpen={showClientSelector}
        onClose={() => {
          setShowClientSelector(false);
          setSelectingPartyIndex(null);
        }}
        onSelectClient={handleSelectClient}
        language={language}
      />
      
      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <SaveTemplateModal
          isOpen={showSaveTemplateModal}
          onClose={() => {
            setShowSaveTemplateModal(false);
            setTemplateName("");
            setTemplateDescription("");
            setIsCabinetWide(false);
          }}
          onSave={handleSaveAsTemplate}
          templateName={templateName}
          setTemplateName={setTemplateName}
          templateDescription={templateDescription}
          setTemplateDescription={setTemplateDescription}
          isCabinetWide={isCabinetWide}
          setIsCabinetWide={setIsCabinetWide}
          language={language}
        />
      )}
      
      {/* Edit Clause Modal */}
      {editingClause && (
        <EditClauseModal
          clause={editingClause}
          onClose={() => setEditingClause(null)}
          onSave={(modifiedContent) => {
            setSelectedClauses(selectedClauses.map(c => 
              c.id === editingClause.id 
                ? { ...c, modified_content: modifiedContent, is_locally_modified: true }
                : c
            ));
            setEditingClause(null);
            toast.success(language === "fr" ? "Clause modifiée" : "Clause modified");
          }}
          language={language}
          getLocalizedText={getLocalizedText}
        />
      )}
    </Layout>
  );
}

// Edit Clause Modal
function EditClauseModal({ clause, onClose, onSave, language, getLocalizedText }) {
  const [content, setContent] = useState(
    clause.modified_content || getLocalizedText(clause.content)
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
      >
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-slate-900">
            {language === "fr" ? "Modifier la clause" : "Edit clause"}
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            {getLocalizedText(clause.title)}
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full h-full min-h-[400px] p-4 border border-slate-300 rounded-lg text-sm font-mono resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div className="p-6 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {language === "fr" ? "Annuler" : "Cancel"}
          </Button>
          <Button onClick={() => onSave(content)}>
            {language === "fr" ? "Enregistrer" : "Save"}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Save Template Modal
function SaveTemplateModal({ 
  isOpen, 
  onClose, 
  onSave, 
  templateName,
  setTemplateName,
  templateDescription,
  setTemplateDescription,
  isCabinetWide,
  setIsCabinetWide,
  language 
}) {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
      >
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-slate-900">
            {language === "fr" ? "Sauvegarder comme template" : "Save as template"}
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            {language === "fr" 
              ? "Créez un template réutilisable (différent du titre du contrat)"
              : "Create a reusable template (different from contract title)"}
          </p>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              {language === "fr" ? "Nom du template" : "Template name"} <span className="text-red-500">*</span>
            </Label>
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder={language === "fr" ? "Ex: NDA Standard Client B2B" : "Ex: Standard B2B Client NDA"}
              className="text-sm"
            />
          </div>

          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              {language === "fr" ? "Description" : "Description"}
            </Label>
            <Input
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder={language === "fr" ? "Ex: Pour clients B2B avec clauses RGPD" : "Ex: For B2B clients with GDPR clauses"}
              className="text-sm"
            />
          </div>

          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <input
              type="checkbox"
              id="cabinet-wide"
              checked={isCabinetWide}
              onChange={(e) => setIsCabinetWide(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600"
            />
            <Label htmlFor="cabinet-wide" className="text-sm text-slate-700 cursor-pointer flex-1">
              {language === "fr" 
                ? "Partager avec tout le cabinet (validation admin)"
                : "Share with entire firm (admin approval)"}
            </Label>
          </div>
        </div>
        
        <div className="p-6 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {language === "fr" ? "Annuler" : "Cancel"}
          </Button>
          <Button onClick={onSave} disabled={!templateName.trim()}>
            {language === "fr" ? "Sauvegarder" : "Save"}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
